import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  District,
  ManagementUnit,
  OperationalArea,
  Permission,
  Role,
  User,
} from '../interfaces/user';
import { BaseService } from './base.service.class';
import { NotificationService } from './notification.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService {
  private userUrl = `${environment.urlBackend}/dhoapi/user`;
  private user = {} as User;
  private roles = [] as Role[];
  private permissions = [] as Permission[];
  private managementUnits = [] as ManagementUnit[];
  private operationalAreas = [] as OperationalArea[];
  private districts = [] as District[];

  constructor(
    private http: HttpClient,
    protected notificationService: NotificationService
  ) {
    super(notificationService);
  }

  getUserRolesPermissions(username: string): Observable<User> {
    const url = `${this.userUrl}?username='${username}'`;
    return this.http.get<User>(url).pipe(
      tap((user) => {
        this.user = user;
        this.loadRolesPermissionsManagementUnits();
      }),
      catchError(this.handleError<User>('getUserRolesPermissions', {}))
    );
  }

  loadRolesPermissionsManagementUnits(): void {
    if (this.user.roles) {
      const roles = this.user.roles;
      for (let index = 0; index < roles.length; index++) {
        const role = roles[index];
        const listPermissions = role.permissions;
        if (listPermissions) {
          for (let indexP = 0; indexP < listPermissions.length; indexP++) {
            const permission = listPermissions[indexP];
            this.permissions.push(permission);
          }
          this.roles.push(role);
          // llenamos arrary de UG
          if (role.managementUnit) {
            // busco la ug en el array
            const ug = this.managementUnits.find(
              (x) => x.managementUnitId == role.managementUnit?.managementUnitId
            );
            // si no encontró el elemento, entonces lo inserto en el array
            if (!ug) {
              this.managementUnits.push(role.managementUnit);
            }
          }
          // llenamos array de Area Operacional
          if (role.operationalArea) {
            this.operationalAreas.push(role.operationalArea);
          }
          // llenamos array de Distritos
          if (role.district) {
            // busco el distrito en el array
            const distrito = this.districts.find(
              (x) => x.districtId == role.district?.districtId
            );
            // si no encontró el elemento, entonces lo inserto en el array
            if (!distrito) {
              this.districts.push(role.district);
            }
          }
        }
      }
    }
  }

  // Devuelve las unidades de gestion del usuario concatenadas en un string, separadas por "coma". Ej: 'NQN,GSJ'
  getManagementUnits(): string {
    let managementUnits = '';
    for (let index = 0; index < this.managementUnits.length; index++) {
      const ug = this.managementUnits[index];
      if (managementUnits == '') {
        managementUnits = ug.managementUnitName as string;
      } else {
        managementUnits = managementUnits + ',' + ug.managementUnitName;
      }
    }
    return managementUnits;
  }

  /**
   * Return the user properties from DHO data base
   */
  getUserProperties(): User {
    return this.user;
  }

  /**
   * return true if user has the permission assigned
   * @param permissionId
   * @returns boolean
   */
  hasPermission(permissionId: number): boolean {
    return (
      this.user.roles!.filter(
        (r) =>
          r.permissions!.filter((p) => p.permissionId == permissionId).length >
          0
      ).length > 0
    );
  }

  /**
   * return true if user has both permission and management unit assigned
   * @param permissionId
   * @param managementUnit
   * @returns boolean
   */
  hasPermissionAndManangementUnit(
    permissionId: number,
    managementUnit: string
  ): boolean {
    let hasPermission = false;
    hasPermission =
      this.user.roles!.filter(
        (r) =>
          r.managementUnit?.managementUnitName == managementUnit &&
          r.permissions!.filter((p) => p.permissionId == permissionId).length >
            0
      ).length > 0;
    return hasPermission;
  }

  /**
   * return true if the user has the operational area assigned
   * @param operationalAreaId
   * @returns boolean
   */
  hasOperationalArea(operationalAreaId: number): boolean {
    return (
      this.user.roles!.filter(
        (r) => r.operationalArea?.operationalAreaId == operationalAreaId
      ).length > 0
    );
  }

  hasOperationalAreaAndDistrict(
    operationalAreaId: number,
    districtId: number
  ): boolean {
    return (
      this.user.roles!.filter(
        (r) =>
          r.operationalArea?.operationalAreaId == operationalAreaId &&
          r.district?.districtId == districtId
      ).length > 0
    );
  }

  hasOnlyOneOperationalArea(): boolean {
    const group = this.operationalAreas.reduce((result: any, oa: any) => {
      const oas = oa.operationalAreaId;
      if (oas != null) {
        if (!result.hasOwnProperty(oas)) {
          result[oas] = 0;
        }
        result[oas]++;
      }
      return result;
    }, {});

    return Object.keys(group).length <= 1;
  }

  hasRolesAssigned(): boolean {
    return this.roles.length > 0;
  }

  hasOperationalAreaAndPermission(
    operationalAreaId: number,
    permissionId: number
  ): boolean {
    return (
      this.user.roles!.filter(
        (r) =>
          r.operationalArea?.operationalAreaId == operationalAreaId &&
          r.permissions!.filter((p) => p.permissionId == permissionId)
      ).length > 0
    );
  }
}
